if (!customElements.get('aspadz-product-main')) {
  customElements.define(
    'aspadz-product-main',
    class AspadzProductMain extends HTMLElement {
      constructor() {
        super();

        this.abortController = null;
        this.variantChangeUnsubscriber = null;
        this.variantMediaShortcutsBound = false;
        this.headerOffsetFrame = null;
        this.headerObserver = null;
        this.stickyBarResizeObserver = null;
        this.gallerySwipeSuppressZoomUntil = 0;
        this.onVariantInputChange = this.onVariantInputChange.bind(this);
        this.onAddonPopupKeydown = this.onAddonPopupKeydown.bind(this);
        this.onAddonPopupSubmit = this.onAddonPopupSubmit.bind(this);
        this.onAddonPopupThumbnailClick = this.onAddonPopupThumbnailClick.bind(this);
        this.onAddonPopupTransitionEnd = this.onAddonPopupTransitionEnd.bind(this);
        this.onCartDrawerOpening = this.onCartDrawerOpening.bind(this);
        this.onSizeFitPopupKeydown = this.onSizeFitPopupKeydown.bind(this);
        this.onSizeFitPopupTransitionEnd = this.onSizeFitPopupTransitionEnd.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        this.abortController = new AbortController();
        document.documentElement.classList.add('aspadz-product-main-page');
        this.gallery = this.querySelector('[data-aspadz-gallery]');
        this.stage = this.querySelector('[data-aspadz-stage]');
        this.stickyBar = this.querySelector('[data-aspadz-sticky-bar]');
        this.productInfo = this.querySelector('[data-aspadz-product-info]');
        this.cartButtons = this.querySelector('.aspadz-product-main__buttons');
        this.addonPopup = this.querySelector('[data-aspadz-addon-popup]');
        this.addonPopupPanel = this.querySelector('[data-aspadz-addon-popup-panel]');
        this.addonPopupFallback = this.parseAddonPopupFallback();
        this.sizeFitPopup = this.querySelector('[data-aspadz-size-fit-popup]');
        this.sizeFitPopupPanel = this.querySelector('[data-aspadz-size-fit-popup-panel]');

        this.bindGallery();
        this.setupStandaloneModelViewers();
        this.bindVariantMediaShortcuts();
        this.filterMediaBySelectedColor();
        this.bindFeatureToggle();
        this.bindDetailsToggle();
        this.bindRideBlocks();
        this.bindAddonPopup();
        this.bindSizeFitPopup();
        this.setupSizeFitPopupTestMode();
        this.bindCartActionButtons();
        this.bindStickyTopButton();
        this.bindImageZoom();
        this.restorePrimarySubmitLabel();
        this.syncSecondarySubmitState();
        this.observeHeaderState();
        this.observeStickyBarFlowSpace();

        if (window.PUB_SUB_EVENTS && typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        document.addEventListener('aspadz:cart-drawer:opening', this.onCartDrawerOpening, {
          signal: this.abortController.signal,
        });
        this.setActiveTab('images');
        this.syncThumbnailRailHeight();
        this.onScroll();
      }

      disconnectedCallback() {
        this.clearAddonPopupCloseTimer();
        this.clearSizeFitPopupCloseTimer();
        this.addonPopupPanel?.removeEventListener('transitionend', this.onAddonPopupTransitionEnd);
        this.sizeFitPopupPanel?.removeEventListener('transitionend', this.onSizeFitPopupTransitionEnd);
        if (
          this.addonPopupOpen ||
          this.sizeFitPopupOpen ||
          document.body.classList.contains('aspadz-product-main-addon-popup-open')
        ) {
          this.unlockAddonPopupScroll();
        }
        this.abortController?.abort();
        this.headerObserver?.disconnect();
        this.stickyBarResizeObserver?.disconnect();
        if (this.headerOffsetFrame) {
          window.cancelAnimationFrame(this.headerOffsetFrame);
          this.headerOffsetFrame = null;
        }
        this.variantChangeUnsubscriber?.();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        this.closeImageZoom();
        if (!document.querySelector('aspadz-product-main')) {
          document.documentElement.classList.remove('aspadz-product-main-page');
          document.documentElement.style.removeProperty('--aspadz-product-main-sticky-flow-space');
        }
      }

      bindGallery() {
        if (!this.gallery) return;

        this.gallery.querySelectorAll('[data-aspadz-gallery-tab]').forEach((button) => {
          button.addEventListener('click', () => this.setActiveTab(button.dataset.aspadzGalleryTab), {
            signal: this.abortController.signal,
          });
        });

        this.gallery.querySelectorAll('[data-aspadz-media-thumb]').forEach((button) => {
          button.addEventListener('click', () => this.activateMedia(button.dataset.mediaId), {
            signal: this.abortController.signal,
          });
        });

        this.gallery.querySelectorAll('[data-aspadz-media-step]').forEach((button) => {
          button.addEventListener('click', () => this.stepMedia(button.dataset.aspadzMediaStep), {
            signal: this.abortController.signal,
          });
        });

        this.gallery.querySelectorAll('[data-aspadz-thumb-scroll]').forEach((button) => {
          button.addEventListener('click', () => this.scrollThumbnails(button.dataset.aspadzThumbScroll), {
            signal: this.abortController.signal,
          });
        });

        this.bindMobileGallerySwipe();
      }

      bindMobileGallerySwipe() {
        const imageStage = this.gallery?.querySelector('[data-aspadz-tab-panel="images"]');
        if (!imageStage) return;

        let touchStart = null;
        const resetTouch = () => {
          touchStart = null;
        };

        imageStage.addEventListener(
          'touchstart',
          (event) => {
            if (!window.matchMedia('(max-width: 749px)').matches || event.touches.length !== 1) {
              resetTouch();
              return;
            }

            const touch = event.touches[0];
            touchStart = { x: touch.clientX, y: touch.clientY };
          },
          { passive: true, signal: this.abortController.signal }
        );

        imageStage.addEventListener(
          'touchend',
          (event) => {
            if (!touchStart || event.changedTouches.length !== 1) {
              resetTouch();
              return;
            }

            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - touchStart.x;
            const deltaY = touch.clientY - touchStart.y;
            resetTouch();

            const horizontalDistance = Math.abs(deltaX);
            const verticalDistance = Math.abs(deltaY);
            const isMobile = window.matchMedia('(max-width: 749px)').matches;
            if (!isMobile || horizontalDistance < 45 || horizontalDistance <= verticalDistance * 1.25) return;

            const didStep = this.stepMedia(deltaX < 0 ? 'next' : 'previous');
            if (!didStep) return;

            // Suppress the synthetic click from opening the zoom dialog after a completed swipe.
            event.preventDefault();
            this.gallerySwipeSuppressZoomUntil = Date.now() + 400;
          },
          { passive: false, signal: this.abortController.signal }
        );

        imageStage.addEventListener('touchcancel', resetTouch, { signal: this.abortController.signal });
      }

      setupStandaloneModelViewers() {
        const viewers = Array.from(this.querySelectorAll('[data-aspadz-standalone-model]'));
        if (!viewers.length || !window.Shopify || typeof Shopify.loadFeatures !== 'function') return;

        Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: (errors) => {
              if (errors || !window.Shopify?.ModelViewerUI) return;

              viewers.forEach((viewer) => {
                if (viewer.dataset.aspadzModelViewerUiReady === 'true') return;

                try {
                  viewer.aspadzModelViewerUI = new Shopify.ModelViewerUI(viewer);
                  viewer.dataset.aspadzModelViewerUiReady = 'true';
                } catch (error) {
                  console.error(error);
                }
              });
            },
          },
        ]);
      }

      bindVariantMediaShortcuts() {
        if (this.variantMediaShortcutsBound) return;

        this.variantMediaShortcutsBound = true;
        this.addEventListener('change', this.onVariantInputChange, { signal: this.abortController.signal });
      }

      onVariantInputChange(event) {
        const input = event.target?.closest?.('[data-aspadz-color-input][data-featured-media-id]');
        if (!input || !this.contains(input) || !input.checked) return;

        this.setActiveTab('images');
        this.filterMediaBySelectedColor(input.value, input.dataset.featuredMediaId);
        this.activateMedia(input.dataset.featuredMediaId);
      }

      bindFeatureToggle() {
        const toggle = this.querySelector('[data-aspadz-features-toggle]');
        if (!toggle) return;

        toggle.addEventListener(
          'click',
          () => {
            const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
            const nextExpanded = !isExpanded;
            const label = toggle.querySelector('[data-aspadz-toggle-label]');

            this.querySelectorAll('[data-aspadz-feature].aspadz-product-main__feature--extra').forEach((item) => {
              item.hidden = !nextExpanded;
            });

            toggle.setAttribute('aria-expanded', String(nextExpanded));
            if (label) {
              label.textContent =
                nextExpanded && toggle.dataset.expandedLabel
                  ? toggle.dataset.expandedLabel
                  : toggle.dataset.collapsedLabel;
            }
          },
          { signal: this.abortController.signal }
        );
      }

      bindDetailsToggle() {
        const toggle = this.querySelector('[data-aspadz-details-toggle]');
        const panel = this.querySelector('[data-aspadz-details-panel]');
        if (!toggle || !panel) return;

        toggle.addEventListener(
          'click',
          () => {
            const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
            const nextExpanded = !isExpanded;
            toggle.setAttribute('aria-expanded', String(nextExpanded));
            panel.hidden = !nextExpanded;
          },
          { signal: this.abortController.signal }
        );
      }

      bindRideBlocks() {
        this.querySelectorAll(
          '.aspadz-product-main__addon-list--bundle, .aspadz-product-main__addon-list--free-essentials'
        ).forEach((list) => {
          this.syncAddonListMobileColumns(list);
        });

        this.querySelectorAll('[data-aspadz-selectable-card]').forEach((card) => {
          card.addEventListener(
            'click',
            () => {
              if (card.disabled || card.classList.contains('is-locked-selected')) return;

              const nextSelected = card.getAttribute('aria-pressed') !== 'true';
              card.setAttribute('aria-pressed', String(nextSelected));
              card.classList.toggle('is-selected', nextSelected);
            },
            { signal: this.abortController.signal }
          );
        });

        this.querySelectorAll('[data-aspadz-collapsible-toggle]').forEach((toggle) => {
          toggle.addEventListener(
            'click',
            () => {
              const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
              const nextExpanded = !isExpanded;
              const container = toggle.closest('[data-aspadz-collapsible]') || this;
              const panel = container.querySelector('[data-aspadz-collapsible-panel]');
              const label = toggle.querySelector('[data-aspadz-toggle-label]');

              if (panel) {
                panel.hidden = !nextExpanded;
              } else {
                container.querySelectorAll('[data-aspadz-collapsible-item]').forEach((item) => {
                  item.hidden = !nextExpanded;
                });
                this.syncAddonListMobileColumns(container.querySelector('.aspadz-product-main__addon-list'));
              }

              toggle.setAttribute('aria-expanded', String(nextExpanded));
              if (label) {
                label.textContent =
                  nextExpanded && toggle.dataset.expandedLabel
                    ? toggle.dataset.expandedLabel
                    : toggle.dataset.collapsedLabel;
              }
            },
            { signal: this.abortController.signal }
          );
        });

        this.querySelectorAll('[data-aspadz-faq-toggle]').forEach((toggle) => {
          toggle.addEventListener(
            'click',
            () => {
              const item = toggle.closest('[data-aspadz-faq-item]');
              const panel = item?.querySelector('[data-aspadz-faq-panel]');
              if (!item || !panel) return;

              const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
              const nextExpanded = !isExpanded;
              toggle.setAttribute('aria-expanded', String(nextExpanded));
              panel.hidden = !nextExpanded;
              item.classList.toggle('is-open', nextExpanded);
            },
            { signal: this.abortController.signal }
          );
        });

        this.querySelectorAll('[data-aspadz-popup-trigger]').forEach((trigger) => {
          trigger.addEventListener(
            'click',
            () => {
              if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;

              if (this.isAddonTriggerSelected(trigger)) {
                this.removeAddonTriggerFromCart(trigger);
                return;
              }

              this.openAddonPopup(trigger);
            },
            { signal: this.abortController.signal }
          );
        });
      }

      syncAddonListMobileColumns(list) {
        if (!list) return;

        list.style.setProperty('--aspadz-addon-mobile-columns', '3');
      }

      bindAddonPopup() {
        if (!this.addonPopup) return;

        this.addonPopup.querySelectorAll('[data-aspadz-addon-popup-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeAddonPopup(), { signal: this.abortController.signal });
        });

        this.addonPopup
          .querySelector('[data-aspadz-addon-popup-submit]')
          ?.addEventListener('click', this.onAddonPopupSubmit, { signal: this.abortController.signal });

        this.addonPopup
          .querySelector('[data-aspadz-addon-popup-thumbs]')
          ?.addEventListener('click', this.onAddonPopupThumbnailClick, { signal: this.abortController.signal });

        document.addEventListener('keydown', this.onAddonPopupKeydown, { signal: this.abortController.signal });
      }

      bindSizeFitPopup() {
        if (!this.sizeFitPopup) return;

        this.querySelectorAll('[data-aspadz-size-fit-trigger]').forEach((trigger) => {
          trigger.addEventListener('click', () => this.openSizeFitPopup(trigger), { signal: this.abortController.signal });
        });

        this.sizeFitPopup.querySelectorAll('[data-aspadz-size-fit-popup-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeSizeFitPopup(), { signal: this.abortController.signal });
        });

        document.addEventListener('keydown', this.onSizeFitPopupKeydown, { signal: this.abortController.signal });
      }

      setupSizeFitPopupTestMode() {
        if (!this.sizeFitPopup || !this.isSizeFitPopupTestMode()) return;

        const trigger = this.querySelector('[data-aspadz-size-fit-trigger]') || this.createSizeFitPopupTestTrigger();

        window.requestAnimationFrame(() => this.openSizeFitPopup(trigger));
      }

      isSizeFitPopupTestMode() {
        const params = new URLSearchParams(window.location.search);

        return params.get('aspadz_size_fit_test') === '1';
      }

      createSizeFitPopupTestTrigger() {
        const trigger = document.createElement('button');

        trigger.type = 'button';
        trigger.className = 'visually-hidden';
        trigger.textContent = 'Open size and fit test popup';
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.sizeFitPopup.id);
        trigger.dataset.aspadzSizeFitTrigger = '';
        this.appendChild(trigger);

        return trigger;
      }

      openSizeFitPopup(trigger) {
        if (!this.sizeFitPopup || !trigger) return;

        this.activeSizeFitTrigger = trigger;
        this.pendingSizeFitPopupFocusTarget = null;
        this.clearSizeFitPopupCloseTimer();
        this.sizeFitPopupPanel?.removeEventListener('transitionend', this.onSizeFitPopupTransitionEnd);
        this.sizeFitPopup.classList.remove('is-closing');
        this.sizeFitPopup.setAttribute('aria-hidden', 'false');
        this.sizeFitPopup.hidden = false;
        this.sizeFitPopupOpen = true;
        this.lockAddonPopupScroll();

        window.requestAnimationFrame(() => {
          this.sizeFitPopup.classList.add('is-open');
          const closeButton = this.sizeFitPopup.querySelector('.aspadz-product-main__addon-popup-close');
          const initialFocus = this.sizeFitPopupPanel || closeButton || this.sizeFitPopup;

          if (typeof trapFocus === 'function') {
            trapFocus(this.sizeFitPopupPanel || this.sizeFitPopup, initialFocus);
          } else {
            initialFocus.focus();
          }
        });
      }

      closeSizeFitPopup(options = {}) {
        if (!this.sizeFitPopup || this.sizeFitPopup.hidden) return;
        if (this.sizeFitPopup.classList.contains('is-closing')) {
          if (options.immediate) this.finishSizeFitPopupClose();
          return;
        }

        const shouldReturnFocus = options.returnFocus !== false;
        const focusTarget = shouldReturnFocus ? this.activeSizeFitTrigger : null;
        this.pendingSizeFitPopupFocusTarget = focusTarget;
        this.sizeFitPopup.classList.remove('is-open');
        this.sizeFitPopup.classList.add('is-closing');
        this.sizeFitPopup.setAttribute('aria-hidden', 'true');
        this.sizeFitPopupOpen = false;

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }

        if (options.immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.finishSizeFitPopupClose();
          return;
        }

        this.sizeFitPopupPanel?.addEventListener('transitionend', this.onSizeFitPopupTransitionEnd);
        this.sizeFitPopupCloseTimer = window.setTimeout(() => this.finishSizeFitPopupClose(), 380);
      }

      onSizeFitPopupTransitionEnd(event) {
        if (event.target !== this.sizeFitPopupPanel || event.propertyName !== 'transform') return;
        this.finishSizeFitPopupClose();
      }

      clearSizeFitPopupCloseTimer() {
        if (!this.sizeFitPopupCloseTimer) return;
        window.clearTimeout(this.sizeFitPopupCloseTimer);
        this.sizeFitPopupCloseTimer = null;
      }

      finishSizeFitPopupClose() {
        if (!this.sizeFitPopup) return;

        this.clearSizeFitPopupCloseTimer();
        this.sizeFitPopupPanel?.removeEventListener('transitionend', this.onSizeFitPopupTransitionEnd);
        this.sizeFitPopup.hidden = true;
        this.sizeFitPopup.classList.remove('is-closing');
        this.unlockAddonPopupScroll();

        const focusTarget = this.pendingSizeFitPopupFocusTarget;
        this.pendingSizeFitPopupFocusTarget = null;
        if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
      }

      onSizeFitPopupKeydown(event) {
        if (!this.sizeFitPopupOpen || event.code !== 'Escape') return;

        event.preventDefault();
        this.closeSizeFitPopup();
      }

      openAddonPopup(trigger) {
        if (!this.addonPopup || !trigger) return;

        const productData = this.getAddonProductData(trigger);
        if (!productData?.variantId) return;

        this.activeAddonTrigger = trigger;
        this.activeAddonProduct = productData;
        this.pendingAddonPopupFocusTarget = null;
        this.clearAddonPopupCloseTimer();
        this.addonPopupPanel?.removeEventListener('transitionend', this.onAddonPopupTransitionEnd);
        this.addonPopup.classList.remove('is-closing');
        this.addonPopup.setAttribute('aria-hidden', 'false');
        this.renderAddonPopup(productData);
        this.hideAddonPopupError();
        this.addonPopup.hidden = false;
        this.addonPopupOpen = true;
        this.lockAddonPopupScroll();

        window.requestAnimationFrame(() => {
          this.addonPopup.classList.add('is-open');
          const closeButton = this.addonPopup.querySelector('.aspadz-product-main__addon-popup-close');
          const initialFocus = this.addonPopupPanel || closeButton || this.addonPopup;

          if (typeof trapFocus === 'function') {
            trapFocus(this.addonPopupPanel || this.addonPopup, initialFocus);
          } else {
            initialFocus.focus();
          }
        });
      }

      closeAddonPopup(options = {}) {
        if (!this.addonPopup || this.addonPopup.hidden) return;
        if (this.addonPopup.classList.contains('is-closing')) {
          if (options.immediate) this.finishAddonPopupClose();
          return;
        }

        const shouldReturnFocus = options.returnFocus !== false;
        const focusTarget = shouldReturnFocus ? this.activeAddonTrigger : null;
        this.pendingAddonPopupFocusTarget = focusTarget;
        this.addonPopup.classList.remove('is-open');
        this.addonPopup.classList.add('is-closing');
        this.addonPopup.setAttribute('aria-hidden', 'true');
        this.addonPopupOpen = false;
        this.hideAddonPopupError();

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }

        if (options.immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.finishAddonPopupClose();
          return;
        }

        this.addonPopupPanel?.addEventListener('transitionend', this.onAddonPopupTransitionEnd);
        this.addonPopupCloseTimer = window.setTimeout(() => this.finishAddonPopupClose(), 380);
      }

      onAddonPopupTransitionEnd(event) {
        if (event.target !== this.addonPopupPanel || event.propertyName !== 'transform') return;
        this.finishAddonPopupClose();
      }

      clearAddonPopupCloseTimer() {
        if (!this.addonPopupCloseTimer) return;
        window.clearTimeout(this.addonPopupCloseTimer);
        this.addonPopupCloseTimer = null;
      }

      finishAddonPopupClose() {
        if (!this.addonPopup) return;

        this.clearAddonPopupCloseTimer();
        this.addonPopupPanel?.removeEventListener('transitionend', this.onAddonPopupTransitionEnd);
        this.addonPopup.hidden = true;
        this.addonPopup.classList.remove('is-closing');
        this.unlockAddonPopupScroll();

        const focusTarget = this.pendingAddonPopupFocusTarget;
        this.pendingAddonPopupFocusTarget = null;
        if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
      }

      onAddonPopupKeydown(event) {
        if (!this.addonPopupOpen || event.code !== 'Escape') return;

        event.preventDefault();
        this.closeAddonPopup();
      }

      onCartDrawerOpening() {
        this.closeAddonPopup({ returnFocus: false, immediate: true });
        this.closeSizeFitPopup({ returnFocus: false, immediate: true });
      }

      onAddonPopupThumbnailClick(event) {
        const button = event.target.closest('[data-aspadz-addon-popup-thumb]');
        if (!button || !this.addonPopup?.contains(button)) return;

        this.setAddonPopupImage(Number(button.dataset.aspadzAddonPopupThumb || 0));
      }

      async onAddonPopupSubmit() {
        const button = this.addonPopup?.querySelector('[data-aspadz-addon-popup-submit]');
        const variantId = this.activeAddonProduct?.variantId;
        if (!button || !variantId || button.getAttribute('aria-disabled') === 'true') return;

        const sections = window.AspadzConceptCart?.getSections?.() || [];
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);

        this.hideAddonPopupError();
        this.setCartActionLoading(button, true);

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });
          const parsedState = await response.json();

          if (theme.utils.isCartResponseError(response, parsedState)) {
            this.handleAddonPopupError(parsedState, variantId);
            return;
          }

          this.markAddonTriggerSelected(this.activeAddonTrigger);
          this.closeAddonPopup({ returnFocus: false });
          await window.AspadzConceptCart?.refresh?.({
            source: 'aspadz-product-main-addon-popup',
            productVariantId: variantId,
            sections: parsedState.sections,
            open: false,
          });
        } catch (error) {
          console.error(error);
          this.showAddonPopupError(this.addonPopup.dataset.errorMessage || '');
        } finally {
          this.setCartActionLoading(button, false);
        }
      }

      getAddonProductData(trigger) {
        const variantId = trigger.dataset.aspadzAddonVariantId;
        if (!variantId) return null;

        const script = Array.from(this.querySelectorAll('[data-aspadz-addon-product-json]')).find(
          (item) => item.dataset.aspadzAddonVariantId === String(variantId)
        );
        const productData = this.parseJsonScript(script);

        return productData
          ? {
              ...productData,
              variantId: String(productData.variantId || variantId),
            }
          : {
              variantId: String(variantId),
              title: trigger.getAttribute('aria-label') || '',
              images: [],
            };
      }

      parseAddonPopupFallback() {
        return this.parseJsonScript(this.addonPopup?.querySelector('[data-aspadz-addon-popup-fallback-json]')) || {
          images: [],
        };
      }

      parseJsonScript(script) {
        if (!script?.textContent) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      renderAddonPopup(productData) {
        if (!this.addonPopup) return;

        const title = this.addonPopup.querySelector('[data-aspadz-addon-popup-title]');
        const price = this.addonPopup.querySelector('[data-aspadz-addon-popup-price]');
        const images = this.getAddonPopupImages(productData);

        if (title) title.textContent = productData.title || '';
        if (price) price.textContent = productData.price || '';

        this.addonPopupImages = images;
        this.renderAddonPopupThumbs(images);
        this.setAddonPopupImage(0);
      }

      getAddonPopupImages(productData) {
        const images = [];
        const addImage = (image) => {
          const src = image?.src || image?.image || image?.thumb;
          const thumb = image?.thumb || src;
          if (!src) return;

          images.push({
            src,
            thumb,
            alt: image.alt || productData.imageAlt || productData.title || this.addonPopupFallback?.imageAlt || '',
          });
        };

        if (Array.isArray(productData.images)) {
          productData.images.forEach(addImage);
        }

        if (!images.length && productData.image) {
          addImage({
            src: productData.image,
            thumb: productData.image,
            alt: productData.imageAlt || productData.title,
          });
        }

        if (!images.length && Array.isArray(this.addonPopupFallback?.images)) {
          this.addonPopupFallback.images.forEach(addImage);
        }

        if (!images.length && this.addonPopupFallback?.image) {
          addImage({
            src: this.addonPopupFallback.image,
            thumb: this.addonPopupFallback.image,
            alt: this.addonPopupFallback.imageAlt || productData.title,
          });
        }

        return images.slice(0, 6);
      }

      renderAddonPopupThumbs(images) {
        const thumbs = this.addonPopup?.querySelector('[data-aspadz-addon-popup-thumbs]');
        if (!thumbs) return;

        thumbs.innerHTML = '';
        thumbs.hidden = images.length === 0;

        images.forEach((image, index) => {
          const button = document.createElement('button');
          const img = document.createElement('img');
          const label = this.addonPopup.dataset.thumbnailLabel || '';

          button.type = 'button';
          button.className = 'aspadz-product-main__addon-popup-thumb';
          button.dataset.aspadzAddonPopupThumb = String(index);
          button.setAttribute('aria-label', label ? `${label} ${index + 1}` : `${index + 1}`);

          img.src = image.thumb;
          img.alt = image.alt || '';
          img.loading = 'lazy';
          button.appendChild(img);
          thumbs.appendChild(button);
        });
      }

      setAddonPopupImage(index) {
        const image = this.addonPopupImages?.[index];
        const imageElement = this.addonPopup?.querySelector('[data-aspadz-addon-popup-image]');
        if (!imageElement) return;

        if (!image) {
          imageElement.hidden = true;
          imageElement.removeAttribute('src');
          imageElement.alt = '';
          return;
        }

        imageElement.hidden = false;
        imageElement.src = image.src;
        imageElement.alt = image.alt || '';

        this.addonPopup.querySelectorAll('[data-aspadz-addon-popup-thumb]').forEach((button) => {
          const isActive = Number(button.dataset.aspadzAddonPopupThumb || 0) === index;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      handleAddonPopupError(response, variantId) {
        const errorMessage = theme.utils.getCartResponseErrorMessage(response);

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'aspadz-product-main-addon-popup',
            productVariantId: variantId,
            errors: response.errors || errorMessage,
            message: response.message || errorMessage,
          });
        }

        this.showAddonPopupError(errorMessage || this.addonPopup.dataset.errorMessage || '');
      }

      showAddonPopupError(message) {
        const error = this.addonPopup?.querySelector('[data-aspadz-addon-popup-error]');
        if (!error) return;

        error.textContent = message;
        error.hidden = !message;
      }

      hideAddonPopupError() {
        const error = this.addonPopup?.querySelector('[data-aspadz-addon-popup-error]');
        if (!error) return;

        error.textContent = '';
        error.hidden = true;
      }

      isAddonTriggerSelected(trigger) {
        return (
          trigger?.classList.contains('is-selected') ||
          trigger?.closest('.aspadz-product-main__bundle-box')?.classList.contains('is-selected')
        );
      }

      markAddonTriggerSelected(trigger) {
        this.setAddonTriggerSelected(trigger, true);
      }

      unmarkAddonTriggerSelected(trigger) {
        this.setAddonTriggerSelected(trigger, false);
      }

      setAddonTriggerSelected(trigger, isSelected) {
        if (!trigger) return;

        trigger.classList.toggle('is-selected', isSelected);
        if (trigger.hasAttribute('aria-pressed')) {
          trigger.setAttribute('aria-pressed', String(isSelected));
        }

        if (trigger.classList.contains('aspadz-product-main__bundle-main')) {
          trigger.closest('.aspadz-product-main__bundle-box')?.classList.toggle('is-selected', isSelected);
        }
      }

      async removeAddonTriggerFromCart(trigger) {
        const variantId = trigger?.dataset.aspadzAddonVariantId;
        if (!variantId) return;

        this.setCartActionLoading(trigger, true);

        try {
          const itemKey = await this.getCartItemKeyForAddonVariant(variantId);
          if (!itemKey) {
            this.unmarkAddonVariantTriggers(variantId);
            return;
          }

          const sections = window.AspadzConceptCart?.getSections?.() || [];
          const response = await fetch(window.routes.cart_change_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              id: itemKey,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            }),
          });
          const parsedState = await response.json();

          if (parsedState.status || parsedState.errors) {
            console.error(parsedState.errors || parsedState.description || parsedState.message);
            return;
          }

          this.unmarkAddonVariantTriggers(variantId);
          await window.AspadzConceptCart?.refresh?.({
            source: 'aspadz-product-main-addon-remove',
            productVariantId: variantId,
            sections: parsedState.sections,
            open: false,
          });
        } catch (error) {
          console.error(error);
        } finally {
          this.setCartActionLoading(trigger, false);
        }
      }

      async getCartItemKeyForAddonVariant(variantId) {
        const response = await fetch(`${window.routes.cart_url}.js`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (!response.ok) return null;

        const cartState = await response.json();
        const item = cartState.items?.find((cartItem) => String(cartItem.variant_id) === String(variantId));
        return item?.key || null;
      }

      unmarkAddonVariantTriggers(variantId) {
        this.querySelectorAll('[data-aspadz-popup-trigger]').forEach((trigger) => {
          if (String(trigger.dataset.aspadzAddonVariantId) === String(variantId)) {
            this.unmarkAddonTriggerSelected(trigger);
          }
        });
      }

      lockAddonPopupScroll() {
        if (this.popupScrollLockActive) return;

        this.popupScrollLockActive = true;
        window.AspadzScrollLock?.lock(this);
        document.documentElement.classList.add('aspadz-product-main-addon-popup-open');
        document.body.classList.add('aspadz-product-main-addon-popup-open');
      }

      unlockAddonPopupScroll() {
        if (!this.popupScrollLockActive && !document.body.classList.contains('aspadz-product-main-addon-popup-open')) return;

        document.documentElement.classList.remove('aspadz-product-main-addon-popup-open');
        document.body.classList.remove('aspadz-product-main-addon-popup-open');
        window.AspadzScrollLock?.unlock(this);
        this.popupScrollLockActive = false;
      }

      bindCartActionButtons() {
        const form = this.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;

        this.querySelectorAll('[data-aspadz-cart-action]').forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              this.pendingCartActionButton = button;
              window.setTimeout(() => {
                if (this.pendingCartActionButton === button) {
                  this.pendingCartActionButton = null;
                }
              }, 0);
            },
            { signal: this.abortController.signal }
          );
        });

        form.addEventListener(
          'submit',
          (event) => {
            const submitter =
              event.submitter ||
              this.pendingCartActionButton ||
              this.querySelector('[data-aspadz-primary-submit]');
            const action = submitter?.dataset.aspadzCartAction || 'cart';

            if (!this.shouldHandleCartAction(action)) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            this.handleCartAction(submitter, action);
          },
          { capture: true, signal: this.abortController.signal }
        );
      }

      bindImageZoom() {
        this.addEventListener(
          'click',
          (event) => {
            const trigger = event.target.closest('[data-aspadz-zoom-trigger]');
            if (!trigger || !this.contains(trigger)) return;
            if (Date.now() < this.gallerySwipeSuppressZoomUntil) {
              event.preventDefault();
              return;
            }
            this.openImageZoom(trigger);
          },
          { signal: this.abortController.signal }
        );
      }

      getZoomImages() {
        return Array.from(
          this.querySelectorAll('[data-aspadz-media-panel]:not([hidden]) [data-aspadz-zoom-trigger]')
        );
      }

      openImageZoom(trigger) {
        if (this.zoomOverlay) return;

        const images = this.getZoomImages();
        const currentIndex = images.indexOf(trigger);

        const overlay = document.createElement('div');
        overlay.className = 'aspadz-product-main__zoom-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', '图片全屏查看');
        overlay.innerHTML = `
          <button class="aspadz-product-main__zoom-close" aria-label="关闭" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <button class="aspadz-product-main__zoom-nav aspadz-product-main__zoom-nav--prev" aria-label="上一张" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div class="aspadz-product-main__zoom-stage">
            <img class="aspadz-product-main__zoom-image" src="" alt="" />
          </div>
          <button class="aspadz-product-main__zoom-nav aspadz-product-main__zoom-nav--next" aria-label="下一张" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <div class="aspadz-product-main__zoom-counter"></div>
        `;

        document.body.appendChild(overlay);
        document.body.classList.add('aspadz-product-main-zoom-open');
        this.zoomOverlay = overlay;
        this.zoomImages = images;
        this.zoomIndex = currentIndex >= 0 ? currentIndex : 0;

        this.zoomNavigate(0);

        // Touch/swipe
        let touchStartX = 0;
        overlay.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
        overlay.addEventListener('touchend', (e) => {
          const delta = touchStartX - e.changedTouches[0].clientX;
          if (Math.abs(delta) > 50) this.zoomNavigate(delta > 0 ? 1 : -1);
        });

        overlay.querySelector('.aspadz-product-main__zoom-close').addEventListener('click', () => this.closeImageZoom());
        overlay.querySelector('.aspadz-product-main__zoom-nav--prev').addEventListener('click', () => this.zoomNavigate(-1));
        overlay.querySelector('.aspadz-product-main__zoom-nav--next').addEventListener('click', () => this.zoomNavigate(1));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeImageZoom(); });

        this.zoomKeyHandler = (e) => {
          if (e.key === 'Escape') this.closeImageZoom();
          if (e.key === 'ArrowLeft') this.zoomNavigate(-1);
          if (e.key === 'ArrowRight') this.zoomNavigate(1);
        };
        document.addEventListener('keydown', this.zoomKeyHandler);

        window.requestAnimationFrame(() => overlay.classList.add('is-open'));
        overlay.querySelector('.aspadz-product-main__zoom-close').focus();
      }

      zoomNavigate(delta) {
        if (!this.zoomOverlay || !this.zoomImages?.length) return;

        this.zoomIndex = (this.zoomIndex + delta + this.zoomImages.length) % this.zoomImages.length;
        const trigger = this.zoomImages[this.zoomIndex];
        const img = this.zoomOverlay.querySelector('.aspadz-product-main__zoom-image');
        const counter = this.zoomOverlay.querySelector('.aspadz-product-main__zoom-counter');
        const prevBtn = this.zoomOverlay.querySelector('.aspadz-product-main__zoom-nav--prev');
        const nextBtn = this.zoomOverlay.querySelector('.aspadz-product-main__zoom-nav--next');

        if (img) {
          img.src = trigger.dataset.zoomSrc || trigger.querySelector('img')?.src || '';
          img.alt = trigger.dataset.zoomAlt || '';
        }
        if (counter) counter.textContent = `${this.zoomIndex + 1} / ${this.zoomImages.length}`;

        const isSingle = this.zoomImages.length <= 1;
        if (prevBtn) prevBtn.hidden = isSingle;
        if (nextBtn) nextBtn.hidden = isSingle;
      }

      closeImageZoom() {
        if (!this.zoomOverlay) return;

        const overlay = this.zoomOverlay;
        this.zoomOverlay = null;
        this.zoomImages = null;

        if (this.zoomKeyHandler) {
          document.removeEventListener('keydown', this.zoomKeyHandler);
          this.zoomKeyHandler = null;
        }

        overlay.classList.remove('is-open');
        document.body.classList.remove('aspadz-product-main-zoom-open');

        const onEnd = () => { overlay.remove(); };
        overlay.addEventListener('transitionend', onEnd, { once: true });
        window.setTimeout(onEnd, 350);
      }

      bindStickyTopButton() {
        const topButton = this.querySelector('[data-aspadz-sticky-top]');
        if (!topButton) return;

        topButton.addEventListener(
          'click',
          () => {
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({
              top: 0,
              behavior: prefersReducedMotion ? 'auto' : 'smooth',
            });
          },
          { signal: this.abortController.signal }
        );
      }

      shouldHandleCartAction(action) {
        return action === 'checkout' || this.getSelectedBundleItems().length > 0;
      }

      async handleCartAction(button, action = 'cart') {
        const form = button?.dataset.aspadzSubmitForm
          ? document.getElementById(button.dataset.aspadzSubmitForm)
          : this.querySelector('form[data-type="add-to-cart-form"]');
        if (!form || button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;

        const variantInput = form.querySelector('[name="id"]');
        const mainVariantId = variantInput?.value;
        if (!mainVariantId) return;

        const items = this.getCartActionItems(mainVariantId);
        const sections = window.AspadzConceptCart?.getSections?.() || [];
        let isNavigatingToCheckout = false;

        this.setCartActionLoading(button, true);

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname,
            }),
          });
          const parsedState = await response.json();

          if (theme.utils.isCartResponseError(response, parsedState)) {
            this.handleCartActionError(parsedState, mainVariantId);
            return;
          }

          if (action === 'checkout') {
            isNavigatingToCheckout = true;
            window.location.href = '/checkout';
            return;
          }

          if (window.AspadzConceptCart) {
            await window.AspadzConceptCart.refresh({
              source: 'aspadz-product-main',
              productVariantId: mainVariantId,
              sections: parsedState.sections,
              open: true,
            });
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (error) {
          console.error(error);
        } finally {
          if (!isNavigatingToCheckout) {
            this.setCartActionLoading(button, false);
          }
        }
      }

      getCartActionItems(mainVariantId) {
        const itemIds = [String(mainVariantId)];

        this.getSelectedBundleItems().forEach((bundleItem) => {
          const variantId = bundleItem.dataset.aspadzAddonVariantId;
          if (variantId && !itemIds.includes(String(variantId))) {
            itemIds.push(String(variantId));
          }
        });

        return itemIds.map((id) => ({
          id: Number(id),
          quantity: 1,
        }));
      }

      getSelectedBundleItems() {
        return Array.from(this.querySelectorAll('[data-aspadz-bundle-item][aria-pressed="true"]')).filter(
          (item) => !item.disabled && item.dataset.aspadzAddonVariantId
        );
      }

      setCartActionLoading(button, isLoading) {
        if (!button) return;

        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-busy');
        }
        button.classList.toggle('loading', isLoading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      handleCartActionError(response, variantId) {
        const errorMessage = theme.utils.getCartResponseErrorMessage(response);

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'aspadz-product-main',
            productVariantId: variantId,
            errors: response.errors || errorMessage,
            message: response.message || errorMessage,
          });
        }

        const productForm = this.querySelector('form[is="product-form"]');
        productForm?.handleErrorMessage?.(errorMessage);
      }

      setActiveTab(tabName) {
        if (!this.gallery) return;

        const nextTab = tabName;
        this.gallery.dataset.activeTab = nextTab;

        this.gallery.querySelectorAll('[data-aspadz-gallery-tab]').forEach((button) => {
          const isActive = button.dataset.aspadzGalleryTab === nextTab;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', String(isActive));
        });

        this.gallery.querySelectorAll('[data-aspadz-tab-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.aspadzTabPanel !== nextTab;
        });

        if (nextTab !== 'video') {
          this.gallery.querySelectorAll('video').forEach((video) => video.pause());
        } else {
          this.gallery.querySelectorAll('[data-aspadz-tab-panel="video"] video').forEach((video) => {
            video.play().catch(() => {});
          });
        }

        this.syncThumbnailRailHeight();
      }

      activateMedia(mediaId) {
        if (!mediaId || !this.gallery) return;

        const targetPanel = this.gallery.querySelector(`[data-aspadz-media-panel][data-media-id="${mediaId}"]`);
        if (!targetPanel || targetPanel.hidden) return;

        this.gallery.querySelectorAll('[data-aspadz-media-panel]').forEach((panel) => {
          panel.classList.toggle('is-active', panel === targetPanel);
        });

        this.gallery.querySelectorAll('[data-aspadz-media-thumb]').forEach((thumb) => {
          const isActive = thumb.dataset.mediaId === mediaId;
          thumb.classList.toggle('is-active', isActive);
          if (isActive) {
            thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        });
      }

      stepMedia(direction) {
        if (!this.gallery || this.gallery.dataset.activeTab !== 'images') return false;

        const panels = Array.from(this.gallery.querySelectorAll('[data-aspadz-media-panel]')).filter(
          (panel) => !panel.hidden
        );
        if (panels.length < 2) return false;

        const currentIndex = Math.max(
          0,
          panels.findIndex((panel) => panel.classList.contains('is-active'))
        );
        const step = direction === 'previous' ? -1 : 1;
        const nextIndex = (currentIndex + step + panels.length) % panels.length;
        this.activateMedia(panels[nextIndex].dataset.mediaId);
        return true;
      }

      scrollThumbnails(direction) {
        const list = this.querySelector('[data-aspadz-thumb-list]');
        if (!list) return;

        const distance = list.querySelector('[data-aspadz-media-thumb]:not([hidden])')?.offsetHeight || 80;
        list.scrollBy({
          top: direction === 'previous' ? -distance : distance,
          behavior: 'smooth',
        });
      }

      filterMediaBySelectedColor(colorValue = this.getSelectedColorValue(), preferredMediaId = null) {
        if (!this.gallery) return;

        const panels = Array.from(this.gallery.querySelectorAll('[data-aspadz-media-panel]'));
        const thumbs = Array.from(this.gallery.querySelectorAll('[data-aspadz-media-thumb]'));
        const normalizedColor = this.normalizeMediaColor(colorValue);
        const matchingPanels = normalizedColor
          ? panels.filter((panel) => this.mediaMatchesColor(panel, normalizedColor))
          : [];
        const visiblePanels = matchingPanels.length ? matchingPanels : panels;
        const visibleMediaIds = new Set(visiblePanels.map((panel) => panel.dataset.mediaId));

        panels.forEach((panel) => {
          const isVisible = visibleMediaIds.has(panel.dataset.mediaId);
          panel.hidden = !isVisible;
          if (!isVisible) panel.classList.remove('is-active');
        });

        thumbs.forEach((thumb) => {
          const isVisible = visibleMediaIds.has(thumb.dataset.mediaId);
          thumb.hidden = !isVisible;
          if (!isVisible) thumb.classList.remove('is-active');
        });

        const preferredId = preferredMediaId ? String(preferredMediaId) : '';
        const currentVisibleActive = this.gallery.querySelector('[data-aspadz-media-panel].is-active:not([hidden])');
        const fallbackPanel = visiblePanels[0];
        const nextMediaId =
          preferredId && visibleMediaIds.has(preferredId)
            ? preferredId
            : currentVisibleActive?.dataset.mediaId || fallbackPanel?.dataset.mediaId;

        if (nextMediaId) {
          this.activateMedia(nextMediaId);
        }

        this.gallery.dataset.currentColor = colorValue || '';
      }

      getSelectedColorValue() {
        return this.querySelector('[data-aspadz-color-input]:checked')?.value || '';
      }

      getVariantColorValue(variant) {
        if (!variant || !this.gallery) return this.getSelectedColorValue();

        const colorOptionPosition = Number(this.gallery.dataset.colorOptionPosition || 0);
        if (!colorOptionPosition) return this.getSelectedColorValue();

        return (
          variant[`option${colorOptionPosition}`] ||
          variant.options?.[colorOptionPosition - 1] ||
          this.getSelectedColorValue()
        );
      }

      mediaMatchesColor(mediaNode, normalizedColor) {
        const mediaColor = this.normalizeMediaColor(mediaNode.dataset.aspadzMediaColor);
        return Boolean(mediaColor && (mediaColor === normalizedColor || mediaColor.includes(normalizedColor)));
      }

      normalizeMediaColor(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/&amp;/g, '&')
          .replace(/[^a-z0-9]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }

      onVariantChange(event) {
        const data = event?.data;
        if (!data || data.sectionId !== this.dataset.sectionId) return;

        if (data.variant?.featured_media?.id) {
          const colorValue = this.getVariantColorValue(data.variant);
          this.setActiveTab('images');
          this.filterMediaBySelectedColor(colorValue, String(data.variant.featured_media.id));
          this.activateMedia(String(data.variant.featured_media.id));
        } else {
          this.filterMediaBySelectedColor();
        }

        const parsedHTML = data.parsedHTML || data.html;
        this.updateStickyPrice(parsedHTML);
        this.updatePromoBadges(parsedHTML);
        this.bindVariantMediaShortcuts();
        window.requestAnimationFrame(() => {
          this.restorePrimarySubmitLabel();
          this.syncSecondarySubmitState(data.variant);
        });
      }

      updateStickyPrice(html) {
        const source = html?.getElementById(`AspadzProductStickyPrice-${this.dataset.sectionId}`);
        const destination = this.querySelector(`#AspadzProductStickyPrice-${this.dataset.sectionId}`);
        if (!source || !destination) return;

        destination.innerHTML = source.innerHTML;
      }

      updatePromoBadges(html) {
        const id = `AspadzProductPromoBadges-${this.dataset.sectionId}`;
        const source = html?.getElementById(id);
        const destination = this.querySelector(`#${id}`);
        if (!source || !destination) return;

        destination.className = source.className;
        destination.innerHTML = source.innerHTML;
        destination.hidden = source.hidden;
      }

      restorePrimarySubmitLabel() {
        const primaryButton = this.querySelector('[data-aspadz-primary-submit]');
        const label = primaryButton?.querySelector('.btn-text > span');
        if (!primaryButton || !label || primaryButton.disabled || !primaryButton.dataset.defaultLabel) return;

        label.textContent = primaryButton.dataset.defaultLabel;
      }

      syncSecondarySubmitState(variant = null) {
        const primaryButton = this.querySelector('[data-aspadz-primary-submit]');
        const disabled = primaryButton?.disabled || variant?.available === false;

        this.querySelectorAll('[data-aspadz-secondary-submit], [data-aspadz-sticky-bar] [data-aspadz-cart-action]').forEach(
          (button) => {
            button.disabled = Boolean(disabled);
          }
        );
      }

      observeHeaderState() {
        const header = document.querySelector('.section-header');

        if (header && typeof MutationObserver === 'function') {
          this.headerObserver = new MutationObserver(() => this.queueStickyMediaOffsetUpdate());
          this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
        }

        this.queueStickyMediaOffsetUpdate();
      }

      queueStickyMediaOffsetUpdate() {
        if (this.headerOffsetFrame) return;

        this.headerOffsetFrame = window.requestAnimationFrame(() => {
          this.headerOffsetFrame = null;
          this.updateStickyMediaOffset();
        });
      }

      updateStickyMediaOffset() {
        const stickyHeader = document.querySelector('.section-header.shopify-section-header-sticky');
        let visibleHeaderHeight = 0;

        if (stickyHeader) {
          const headerIsHidden =
            stickyHeader.classList.contains('shopify-section-header-hidden') &&
            !stickyHeader.classList.contains('menu-open');

          if (!headerIsHidden) {
            const headerHeight = parseFloat(
              window.getComputedStyle(document.documentElement).getPropertyValue('--header-height')
            );

            visibleHeaderHeight = Number.isFinite(headerHeight) ? headerHeight : stickyHeader.getBoundingClientRect().height;
          }
        }

        this.style.setProperty('--aspadz-product-main-visible-header-height', `${Math.max(0, Math.round(visibleHeaderHeight))}px`);
      }

      syncThumbnailRailHeight() {
        if (!this.gallery || !this.stage || this.isMobileViewport()) return;

        const stageHeight = this.stage.getBoundingClientRect().height;
        if (stageHeight > 0) {
          this.gallery.style.setProperty('--aspadz-product-main-stage-height', `${Math.round(stageHeight)}px`);
        }
      }

      observeStickyBarFlowSpace() {
        if (!this.stickyBar) return;

        if ('ResizeObserver' in window) {
          this.stickyBarResizeObserver = new ResizeObserver(() => this.syncStickyBarFlowSpace());
          this.stickyBarResizeObserver.observe(this.stickyBar);
        }

        this.syncStickyBarFlowSpace();
      }

      syncStickyBarFlowSpace() {
        if (!this.stickyBar) return;

        const stickyHeight = Math.ceil(this.stickyBar.getBoundingClientRect().height);

        if (stickyHeight > 0) {
          document.documentElement.style.setProperty('--aspadz-product-main-sticky-flow-space', `${stickyHeight}px`);
        }
      }

      onScroll() {
        this.queueStickyMediaOffsetUpdate();

        if (!this.stickyBar) return;

        const trigger = this.cartButtons || this.productInfo;
        if (!trigger) return;

        const shouldShow = trigger.getBoundingClientRect().bottom < 0;
        this.stickyBar.classList.toggle('is-visible', shouldShow);
        this.stickyBar.setAttribute('aria-hidden', String(!shouldShow));
      }

      onResize() {
        this.queueStickyMediaOffsetUpdate();
        this.syncThumbnailRailHeight();
        this.syncStickyBarFlowSpace();

      }

      isMobileViewport() {
        return window.matchMedia('(max-width: 749px)').matches;
      }
    }
  );
}
